import { paymentTierAmount, type PaymentTier } from "@/types/payments";

export interface BundleEntitlement {
  /** Stable id used to look up display copy for this entitlement. */
  key: string;
  codePrefix: string;
  description: string;
  applicableTier: PaymentTier;
  quota: number;
}

/**
 * The Career bundle (79k "Career OS") grants these unique, per-user, fully
 * discounted vouchers. This is the single source of truth for what a buyer
 * receives: the voucher generator issues from it and the checkout view renders
 * its quotas, so the advertised offer cannot drift from the granted one.
 *
 * DB-free on purpose, so client components can import it.
 */
export const CAREER_BUNDLE_CATALOG: BundleEntitlement[] = [
  {
    key: "cv_review_tailored",
    codePrefix: "CV",
    description: "Career Bundle: CV Review & CV Tailored",
    applicableTier: "Ready to Use CV in 3 Different Format",
    quota: 5,
  },
  {
    key: "mock_interview",
    codePrefix: "MI",
    description: "Career Bundle: Mock Interview AI",
    applicableTier: "Interview Session",
    quota: 2,
  },
  {
    key: "cv_personalization",
    codePrefix: "CP",
    description: "Career Bundle: CV Personalization",
    applicableTier: "Career OS - CV Personalization",
    quota: 1,
  },
  {
    key: "linkedin_optimization",
    codePrefix: "LI",
    description: "Career Bundle: LinkedIn Optimization",
    applicableTier: "Career OS - LinkedIn Optimization",
    quota: 1,
  },
  {
    key: "ideal_job_fit",
    codePrefix: "JF",
    description: "Career Bundle: Ideal Job Fit",
    applicableTier: "Career OS - Ideal Job Fit",
    quota: 1,
  },
];

/**
 * Renders the entitlements as "<label> x<quota>" from the catalog, so every
 * surface advertising the bundle shows the number of redemptions actually
 * granted. Callers supply localized labels keyed by {@link BundleEntitlement.key};
 * an entitlement with no label is skipped rather than shown untranslated.
 */
export function buildBundleEntitlementLines(
  labels: Record<string, string>,
): string[] {
  return CAREER_BUNDLE_CATALOG.flatMap((item) => {
    const label = labels[item.key];
    return label ? [`${label} x${item.quota}`] : [];
  });
}

/**
 * Full list value of what the catalog grants, at each tier's standalone price.
 * Not advertised: it exists so the pinned anchor below can be checked against
 * it, and so a future repricing shows up here rather than silently.
 */
export const CAREER_BUNDLE_LIST_VALUE = CAREER_BUNDLE_CATALOG.reduce(
  (total, item) => total + paymentTierAmount[item.applicableTier] * item.quota,
  0,
);

/**
 * The advertised "if you bought these separately" price.
 *
 * Deliberately pinned BELOW {@link CAREER_BUNDLE_LIST_VALUE} (Rp500.000 at the
 * current catalog and prices). Understating the comparison is the safe
 * direction, so do not "correct" this to the derived sum: raising it inflates
 * the savings claim. Change it only as a deliberate marketing decision, and
 * update the career-booster landing page (`content.ts` BUNDLE_ANCHOR and its
 * "hemat NN%" copy) in the same change so the surfaces agree.
 */
export const CAREER_BUNDLE_ANCHOR_AMOUNT = 350_000;

/**
 * Advertised saving against the anchor, rounded *down* so the claim is never
 * overstated.
 */
export function careerBundleSavingsPercent(bundlePrice: number): number {
  const saved = CAREER_BUNDLE_ANCHOR_AMOUNT - bundlePrice;

  // Also covers a priced-at-or-above-anchor bundle: no saving to advertise.
  if (saved <= 0) {
    return 0;
  }

  return Math.floor((saved / CAREER_BUNDLE_ANCHOR_AMOUNT) * 100);
}
